import React, { useCallback, useEffect, useRef, useState } from "react";

// Four free corners over an image; the API straightens the quad they enclose
// (POST /admin/cover-curation/:id/faces/:face/crop). Points are [x, y] in 0..1 of the
// image, clockwise from top-left. Drag a corner, or focus it and nudge with the arrow
// keys (Shift for bigger steps). onPointsChange and onImageLoad (natural size) let the
// caller follow the crop; children render above the actions.
const CORNER_LABELS = ["top-left", "top-right", "bottom-right", "bottom-left"];
const DEFAULT_POINTS = [[0.05, 0.05], [0.95, 0.05], [0.95, 0.95], [0.05, 0.95]];
const NUDGE = 0.002;
const NUDGE_LARGE = 0.01;

const clamp = (value) => Math.min(1, Math.max(0, value));

function PerspectiveCropEditor({ src, initialPoints, busy, onApply, onCancel, onPointsChange, onImageLoad, children }) {
  const frameRef = useRef(null);
  const [points, setPoints] = useState(initialPoints || DEFAULT_POINTS);
  const [dragging, setDragging] = useState(null);

  useEffect(() => {
    onPointsChange?.(points);
  }, [points, onPointsChange]);

  const moveTo = useCallback((index, clientX, clientY) => {
    const rect = frameRef.current.getBoundingClientRect();
    const next = [clamp((clientX - rect.left) / rect.width), clamp((clientY - rect.top) / rect.height)];
    setPoints((prev) => prev.map((point, i) => (i === index ? next : point)));
  }, []);

  function handleKeyDown(index, e) {
    const step = e.shiftKey ? NUDGE_LARGE : NUDGE;
    const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!delta) return;
    e.preventDefault();
    setPoints((prev) => prev.map((point, i) => (i === index ? [clamp(point[0] + delta[0]), clamp(point[1] + delta[1])] : point)));
  }

  const quad = points.map(([x, y]) => `${x} ${y}`).join(" L ");

  return (
    <div className="admin-crop">
      <div className="admin-crop-stage">
        <div className="admin-crop-frame" ref={frameRef}>
          <img
            className="admin-crop-image"
            src={src}
            alt="Crop source"
            draggable={false}
            onLoad={(e) => onImageLoad?.({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          />
          <svg className="admin-crop-overlay" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <path className="admin-crop-shade" d={`M0 0 H1 V1 H0 Z M ${quad} Z`} fillRule="evenodd" />
            <path className="admin-crop-outline" d={`M ${quad} Z`} vectorEffect="non-scaling-stroke" />
          </svg>
          {points.map(([x, y], index) => (
            <button
              type="button"
              key={CORNER_LABELS[index]}
              className={`admin-crop-handle ${dragging === index ? "admin-crop-handle--active" : ""}`}
              style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
              aria-label={`Move ${CORNER_LABELS[index]} corner`}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                setDragging(index);
              }}
              onPointerMove={(e) => {
                if (dragging === index) moveTo(index, e.clientX, e.clientY);
              }}
              onPointerUp={() => setDragging(null)}
              onPointerCancel={() => setDragging(null)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={busy}
            />
          ))}
        </div>
      </div>

      {children}

      <div className="admin-row-actions admin-row-actions--gap admin-crop-actions">
        <button type="button" className="admin-signout-btn" onClick={() => setPoints(DEFAULT_POINTS)} disabled={busy}>
          Reset corners
        </button>
        <button type="button" className="admin-signout-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="admin-signout-btn admin-crop-apply" onClick={() => onApply(points)} disabled={busy}>
          {busy ? "Cropping…" : "Apply crop"}
        </button>
      </div>
    </div>
  );
}

export default PerspectiveCropEditor;
